import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, BadgeCheck, Instagram } from 'lucide-react';
import { CAT_COLOR, formatDate } from '../pages/Blog';
import { thumb, thumbSet } from '../lib/img';
import { COMPANY } from '../data/legalContent';

const API = process.env.REACT_APP_BACKEND_URL;
const IG_URL = 'https://www.instagram.com/dysscindia/';
const AVATAR = `${API}/api/static/brand/ig_d_logo.png`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };

const LatestBlogCard = ({ post }) => (
  <motion.div variants={fadeUp} className="flex flex-col w-full max-w-xl">
    <Link
      to={`/blog/${post.slug}`}
      data-testid="home-latest-blog-card"
      className="group flex flex-col sm:flex-row rounded-2xl overflow-hidden border border-zinc-800 bg-[#111214] hover:border-zinc-600 transition-colors duration-300"
    >
      {/* Covers are 3:4 portrait — show the whole image, never crop */}
      <div className="sm:w-[46%] shrink-0 bg-[#0B0B0C] aspect-[3/4] overflow-hidden" data-testid="latest-blog-cover">
        <img
          src={thumb(post.cover, 480)}
          srcSet={thumbSet(post.cover, [360, 480, 720])}
          sizes="(min-width:640px) 320px, 92vw"
          alt={post.title}
          width="1080" height="1440" decoding="async" loading="lazy"
          className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-700"
        />
      </div>
      <div className="p-5 sm:p-6 flex flex-col justify-center min-w-0">
        <div className="flex flex-wrap items-center gap-2.5 text-[11px]">
          <span className={`px-2 py-0.5 rounded-full border ${CAT_COLOR[post.category] || 'text-zinc-400 border-zinc-700 bg-zinc-800/40'}`}>
            {post.category}
          </span>
          <span className="text-zinc-500">{formatDate(post.published_at)}</span>
          {post.read_time && (
            <span className="text-zinc-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />{post.read_time} min read
            </span>
          )}
        </div>
        <h3 className="font-display text-xl md:text-2xl text-white mt-3 leading-tight">{post.title}</h3>
        <p className="text-zinc-400 mt-3 text-sm leading-relaxed line-clamp-3 sm:line-clamp-4">{post.excerpt}</p>
        <span className="inline-flex items-center gap-2 text-[#E02128] text-xs font-semibold mt-5 group-hover:gap-3 transition-all duration-300" data-testid="latest-blog-read-link">
          Read article <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
    <Link
      to="/blog"
      data-testid="latest-blog-viewall-link"
      className="mt-4 inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs transition-colors self-start"
    >
      View all articles <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </motion.div>
);

const IgBanner = () => (
  <motion.a
    variants={fadeUp}
    href={COMPANY.social.instagram}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="social-instagram"
    className="group flex items-center gap-4 rounded-xl p-4 text-white relative overflow-hidden hover:shadow-xl hover:shadow-[#d62976]/25 hover:-translate-y-1 transition-all duration-300"
    style={{ background: 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)' }}
  >
    <span className="ig-shine" />
    <div className="relative w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
      <Instagram className="w-5.5 h-5.5 text-white" style={{ width: 22, height: 22 }} />
    </div>
    <div className="min-w-0 relative">
      <p className="text-[10px] uppercase tracking-widest font-semibold text-white/80">Follow on Instagram</p>
      <p className="font-display text-lg tracking-tight leading-tight">{COMPANY.instagramHandle}</p>
      <p className="text-[11px] text-white/90 mt-0.5">Tag us #DYSSC</p>
    </div>
    <span className="relative ml-auto text-xs font-semibold bg-white text-[#d62976] rounded-full px-3.5 py-1.5 group-hover:scale-105 transition-transform duration-300 shrink-0">
      Follow
    </span>
  </motion.a>
);

const StatusIcons = () => (
  <svg width="46" height="12" viewBox="0 0 46 12" fill="currentColor" aria-hidden="true">
    <rect x="0" y="7" width="2.5" height="4" rx="0.6" /><rect x="4" y="5" width="2.5" height="6" rx="0.6" />
    <rect x="8" y="3" width="2.5" height="8" rx="0.6" /><rect x="12" y="1" width="2.5" height="10" rx="0.6" />
    <path d="M24 4.2a7.2 7.2 0 0 1 5 2l-1.3 1.4a5.3 5.3 0 0 0-7.4 0L19 6.2a7.2 7.2 0 0 1 5-2Zm0 3.4c.9 0 1.8.36 2.4 1l-2.4 2.5-2.4-2.5a3.4 3.4 0 0 1 2.4-1Z" />
    <rect x="33" y="2.5" width="11" height="7" rx="1.8" fill="none" stroke="currentColor" />
    <rect x="34.5" y="4" width="7" height="4" rx="0.8" /><rect x="44.7" y="4.6" width="1.3" height="2.8" rx="0.6" />
  </svg>
);

const PhoneMockup = ({ tiles }) => (
  <motion.a
    variants={fadeUp}
    href={IG_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="home-ig-phone"
    aria-label="Follow @dysscindia on Instagram"
    className="relative block mx-auto w-[224px] sm:w-[248px]"
  >
    <div className="absolute -inset-12 bg-[#E02128]/10 blur-3xl rounded-full pointer-events-none" />
    <div className="relative rounded-[48px] bg-zinc-900 p-[10px] border border-zinc-700/70 shadow-2xl shadow-black/70 hover:-translate-y-1.5 transition-transform duration-500">
      <div className="relative rounded-[38px] overflow-hidden bg-white flex flex-col h-[448px] sm:h-[480px]">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[84px] h-[20px] bg-black rounded-full z-10" />
        <div className="flex items-center justify-between px-7 pt-3 pb-1 text-[11px] font-semibold text-zinc-900">
          <span>9:41</span>
          <StatusIcons />
        </div>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100">
          <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center ring-2 ring-[#E02128]/70 ring-offset-2 shrink-0">
            <img src={AVATAR} alt="DYSSC logo" className="w-6 h-auto" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-bold text-zinc-900 flex items-center gap-1">
              dysscindia <BadgeCheck className="w-3.5 h-3.5 text-[#0095F6]" />
            </p>
            <p className="text-[11px] text-zinc-500 truncate">DYSSC · Pickleball Paddles · India</p>
          </div>
          <span data-testid="ig-follow-btn" className="bg-[#0095F6] text-white text-[12px] font-semibold px-3.5 py-1.5 rounded-lg">
            Follow
          </span>
        </div>
        <div className="relative flex-1 overflow-hidden bg-white" data-testid="ig-feed-scroller">
          <div className="ig-feed-scroll">
            {[0, 1].map((pass) => (
              <div key={pass} className="grid grid-cols-3 gap-0.5 pb-0.5" aria-hidden={pass === 1}>
                {tiles.map((t, i) => (
                  <img
                    key={`${pass}-${i}`}
                    src={`${API}${t.ig_image}`}
                    alt={pass === 0 ? 'DYSSC Instagram post' : ''}
                    className="aspect-square w-full object-cover"
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white/80 to-transparent" />
        </div>
        <div className="h-10 border-t border-zinc-100 flex items-center justify-center bg-white">
          <p className="text-[11px] text-zinc-500 font-medium">
            Follow <span className="text-zinc-900 font-semibold">@dysscindia</span> on Instagram
          </p>
        </div>
      </div>
    </div>
  </motion.a>
);

export const JournalFeedSection = () => {
  const [data, setData] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('home_feed_v1')) || null; } catch { return null; }
  });

  useEffect(() => {
    fetch(`${API}/api/home/feed`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        try { sessionStorage.setItem('home_feed_v1', JSON.stringify(d)); } catch { /* quota */ }
      })
      .catch((e) => console.error('home feed load failed', e));
  }, []);

  if (!data || !data.post) return null;
  const base = (data.ig || []).filter((t) => t.ig_image);
  let tiles = [];
  while (tiles.length < 12 && base.length) tiles = tiles.concat(base);

  return (
    <section className="py-12 md:py-16 bg-[#0B0B0C] bg-texture" id="journal" data-testid="home-journal-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <div className={`grid grid-cols-1 gap-8 lg:gap-12 items-start ${tiles.length ? 'lg:grid-cols-2' : 'max-w-2xl mx-auto'}`}>
            <div className="flex flex-col items-start">
              <motion.div variants={fadeUp} className="mb-8 md:mb-10 max-w-xl">
                <p className="uppercase text-xs tracking-widest font-semibold text-[#E02128] mb-3">Fresh off the court</p>
                <h2 className="font-display text-4xl md:text-5xl tracking-tight text-white">The Journal &amp; the Feed</h2>
                <p className="text-zinc-400 leading-relaxed mt-4 text-base">
                  Our latest deep-dive from the DYSSC Journal — and what we&apos;re posting on Instagram right now.
                </p>
              </motion.div>
              <LatestBlogCard post={data.post} />
            </div>
            {tiles.length > 0 && (
              <div className="max-w-[380px] mx-auto w-full space-y-8">
                <IgBanner />
                <PhoneMockup tiles={tiles} />
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
